import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class ResultSetup extends BaseObj {
    public ID: number;
    public __id: number;

    public ColumnAfter: boolean;
    public __columnafter: boolean;

    public ColumnBib: boolean;
    public __columnbib: boolean;

    public ColumnCategory: boolean;
    public __columncategory: boolean;

    public ColumnCategoryGroup: boolean;
    public __columncategorygroup: boolean;

    public ColumnClub: boolean;
    public __columnclub: boolean;

    public ColumnRanking: boolean;
    public __columnranking: boolean;

    public ColumnShowClockTime: boolean;
    public __columnshowclocktime: boolean;

    public ColumnShowDistance: boolean;
    public __columnshowdistance: boolean;

    public ColumnShowLaps: boolean;
    public __columnshowlaps: boolean;

    public ColumnShowTrend: boolean;
    public __columnshowtrend: boolean;

    public ColumnTime: boolean;
    public __columntime: boolean;

    public ColumnTimePenalty: boolean;
    public __columntimepenalty: boolean;

    public DefaultToSplitlist: boolean;
    public __defaulttosplitlist: boolean;

    public EventID: number;
    public __eventid: number;

    public HideSplitList: boolean;
    public __hidesplitlist: boolean;

    public LiveEnabled: boolean;
    public __liveenabled: boolean;

    public LiveShooting: boolean;
    public __liveshooting: boolean;

    public LiveTracking: boolean;
    public __livetracking: boolean;

    public ShowChipsOnLive: boolean;
    public __showchipsonlive: boolean;

    public ShowContestantList: boolean;
    public __showcontestantlist: boolean;

    public ShowDiploma: boolean;
    public __showdiploma: boolean;

    public ShowPanelResult: boolean;
    public __showpanelresult: boolean;

    public ShowTotalLists: boolean;
    public __showtotallists: boolean;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        ColumnAfter?: boolean;
        ColumnBib?: boolean;
        ColumnCategory?: boolean;
        ColumnCategoryGroup?: boolean;
        ColumnClub?: boolean;
        ColumnRanking?: boolean;
        ColumnShowClockTime?: boolean;
        ColumnShowDistance?: boolean;
        ColumnShowLaps?: boolean;
        ColumnShowTrend?: boolean;
        ColumnTime?: boolean;
        ColumnTimePenalty?: boolean;
        DefaultToSplitlist?: boolean;
        EntityState?: EntityState;
        EventID?: number;
        HideSplitList?: boolean;
        LiveEnabled?: boolean;
        LiveShooting?: boolean;
        LiveTracking?: boolean;
        ShowChipsOnLive?: boolean;
        ShowContestantList?: boolean;
        ShowDiploma?: boolean;
        ShowPanelResult?: boolean;
        ShowTotalLists?: boolean;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<ResultSetup>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('ResultSetup tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.ColumnAfter = values.ColumnAfter ?? false;
        this.__columnafter = values.ColumnAfter ?? false;
        Object.defineProperty(this, 'ColumnAfter', {
            get(): boolean {
                return this.__columnafter;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ColumnAfter', this.__columnafter, value);
                this.__columnafter = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ColumnBib = values.ColumnBib ?? false;
        this.__columnbib = values.ColumnBib ?? false;
        Object.defineProperty(this, 'ColumnBib', {
            get(): boolean {
                return this.__columnbib;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ColumnBib', this.__columnbib, value);
                this.__columnbib = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ColumnCategory = values.ColumnCategory ?? false;
        this.__columncategory = values.ColumnCategory ?? false;
        Object.defineProperty(this, 'ColumnCategory', {
            get(): boolean {
                return this.__columncategory;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ColumnCategory', this.__columncategory, value);
                this.__columncategory = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ColumnCategoryGroup = values.ColumnCategoryGroup ?? false;
        this.__columncategorygroup = values.ColumnCategoryGroup ?? false;
        Object.defineProperty(this, 'ColumnCategoryGroup', {
            get(): boolean {
                return this.__columncategorygroup;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ColumnCategoryGroup', this.__columncategorygroup, value);
                this.__columncategorygroup = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ColumnClub = values.ColumnClub ?? false;
        this.__columnclub = values.ColumnClub ?? false;
        Object.defineProperty(this, 'ColumnClub', {
            get(): boolean {
                return this.__columnclub;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ColumnClub', this.__columnclub, value);
                this.__columnclub = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ColumnRanking = values.ColumnRanking ?? false;
        this.__columnranking = values.ColumnRanking ?? false;
        Object.defineProperty(this, 'ColumnRanking', {
            get(): boolean {
                return this.__columnranking;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ColumnRanking', this.__columnranking, value);
                this.__columnranking = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ColumnShowClockTime = values.ColumnShowClockTime ?? false;
        this.__columnshowclocktime = values.ColumnShowClockTime ?? false;
        Object.defineProperty(this, 'ColumnShowClockTime', {
            get(): boolean {
                return this.__columnshowclocktime;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ColumnShowClockTime', this.__columnshowclocktime, value);
                this.__columnshowclocktime = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ColumnShowDistance = values.ColumnShowDistance ?? false;
        this.__columnshowdistance = values.ColumnShowDistance ?? false;
        Object.defineProperty(this, 'ColumnShowDistance', {
            get(): boolean {
                return this.__columnshowdistance;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ColumnShowDistance', this.__columnshowdistance, value);
                this.__columnshowdistance = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ColumnShowLaps = values.ColumnShowLaps ?? false;
        this.__columnshowlaps = values.ColumnShowLaps ?? false;
        Object.defineProperty(this, 'ColumnShowLaps', {
            get(): boolean {
                return this.__columnshowlaps;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ColumnShowLaps', this.__columnshowlaps, value);
                this.__columnshowlaps = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ColumnShowTrend = values.ColumnShowTrend ?? false;
        this.__columnshowtrend = values.ColumnShowTrend ?? false;
        Object.defineProperty(this, 'ColumnShowTrend', {
            get(): boolean {
                return this.__columnshowtrend;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ColumnShowTrend', this.__columnshowtrend, value);
                this.__columnshowtrend = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ColumnTime = values.ColumnTime ?? false;
        this.__columntime = values.ColumnTime ?? false;
        Object.defineProperty(this, 'ColumnTime', {
            get(): boolean {
                return this.__columntime;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ColumnTime', this.__columntime, value);
                this.__columntime = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ColumnTimePenalty = values.ColumnTimePenalty ?? false;
        this.__columntimepenalty = values.ColumnTimePenalty ?? false;
        Object.defineProperty(this, 'ColumnTimePenalty', {
            get(): boolean {
                return this.__columntimepenalty;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ColumnTimePenalty', this.__columntimepenalty, value);
                this.__columntimepenalty = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DefaultToSplitlist = values.DefaultToSplitlist ?? false;
        this.__defaulttosplitlist = values.DefaultToSplitlist ?? false;
        Object.defineProperty(this, 'DefaultToSplitlist', {
            get(): boolean {
                return this.__defaulttosplitlist;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('DefaultToSplitlist', this.__defaulttosplitlist, value);
                this.__defaulttosplitlist = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.HideSplitList = values.HideSplitList ?? false;
        this.__hidesplitlist = values.HideSplitList ?? false;
        Object.defineProperty(this, 'HideSplitList', {
            get(): boolean {
                return this.__hidesplitlist;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('HideSplitList', this.__hidesplitlist, value);
                this.__hidesplitlist = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LiveEnabled = values.LiveEnabled ?? false;
        this.__liveenabled = values.LiveEnabled ?? false;
        Object.defineProperty(this, 'LiveEnabled', {
            get(): boolean {
                return this.__liveenabled;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('LiveEnabled', this.__liveenabled, value);
                this.__liveenabled = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LiveShooting = values.LiveShooting ?? false;
        this.__liveshooting = values.LiveShooting ?? false;
        Object.defineProperty(this, 'LiveShooting', {
            get(): boolean {
                return this.__liveshooting;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('LiveShooting', this.__liveshooting, value);
                this.__liveshooting = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LiveTracking = values.LiveTracking ?? false;
        this.__livetracking = values.LiveTracking ?? false;
        Object.defineProperty(this, 'LiveTracking', {
            get(): boolean {
                return this.__livetracking;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('LiveTracking', this.__livetracking, value);
                this.__livetracking = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ShowChipsOnLive = values.ShowChipsOnLive ?? false;
        this.__showchipsonlive = values.ShowChipsOnLive ?? false;
        Object.defineProperty(this, 'ShowChipsOnLive', {
            get(): boolean {
                return this.__showchipsonlive;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ShowChipsOnLive', this.__showchipsonlive, value);
                this.__showchipsonlive = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ShowContestantList = values.ShowContestantList ?? false;
        this.__showcontestantlist = values.ShowContestantList ?? false;
        Object.defineProperty(this, 'ShowContestantList', {
            get(): boolean {
                return this.__showcontestantlist;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ShowContestantList', this.__showcontestantlist, value);
                this.__showcontestantlist = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ShowDiploma = values.ShowDiploma ?? false;
        this.__showdiploma = values.ShowDiploma ?? false;
        Object.defineProperty(this, 'ShowDiploma', {
            get(): boolean {
                return this.__showdiploma;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ShowDiploma', this.__showdiploma, value);
                this.__showdiploma = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ShowPanelResult = values.ShowPanelResult ?? false;
        this.__showpanelresult = values.ShowPanelResult ?? false;
        Object.defineProperty(this, 'ShowPanelResult', {
            get(): boolean {
                return this.__showpanelresult;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ShowPanelResult', this.__showpanelresult, value);
                this.__showpanelresult = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ShowTotalLists = values.ShowTotalLists ?? false;
        this.__showtotallists = values.ShowTotalLists ?? false;
        Object.defineProperty(this, 'ShowTotalLists', {
            get(): boolean {
                return this.__showtotallists;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ShowTotalLists', this.__showtotallists, value);
                this.__showtotallists = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"ColumnAfter": ${JSON.stringify(this.ColumnAfter)}` +
            ',' +
            `"ColumnBib": ${JSON.stringify(this.ColumnBib)}` +
            ',' +
            `"ColumnCategory": ${JSON.stringify(this.ColumnCategory)}` +
            ',' +
            `"ColumnCategoryGroup": ${JSON.stringify(this.ColumnCategoryGroup)}` +
            ',' +
            `"ColumnClub": ${JSON.stringify(this.ColumnClub)}` +
            ',' +
            `"ColumnRanking": ${JSON.stringify(this.ColumnRanking)}` +
            ',' +
            `"ColumnShowClockTime": ${JSON.stringify(this.ColumnShowClockTime)}` +
            ',' +
            `"ColumnShowDistance": ${JSON.stringify(this.ColumnShowDistance)}` +
            ',' +
            `"ColumnShowLaps": ${JSON.stringify(this.ColumnShowLaps)}` +
            ',' +
            `"ColumnShowTrend": ${JSON.stringify(this.ColumnShowTrend)}` +
            ',' +
            `"ColumnTime": ${JSON.stringify(this.ColumnTime)}` +
            ',' +
            `"ColumnTimePenalty": ${JSON.stringify(this.ColumnTimePenalty)}` +
            ',' +
            `"DefaultToSplitlist": ${JSON.stringify(this.DefaultToSplitlist)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"HideSplitList": ${JSON.stringify(this.HideSplitList)}` +
            ',' +
            `"LiveEnabled": ${JSON.stringify(this.LiveEnabled)}` +
            ',' +
            `"LiveShooting": ${JSON.stringify(this.LiveShooting)}` +
            ',' +
            `"LiveTracking": ${JSON.stringify(this.LiveTracking)}` +
            ',' +
            `"ShowChipsOnLive": ${JSON.stringify(this.ShowChipsOnLive)}` +
            ',' +
            `"ShowContestantList": ${JSON.stringify(this.ShowContestantList)}` +
            ',' +
            `"ShowDiploma": ${JSON.stringify(this.ShowDiploma)}` +
            ',' +
            `"ShowPanelResult": ${JSON.stringify(this.ShowPanelResult)}` +
            ',' +
            `"ShowTotalLists": ${JSON.stringify(this.ShowTotalLists)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of ResultSetup from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        ColumnAfter?: boolean;
        ColumnBib?: boolean;
        ColumnCategory?: boolean;
        ColumnCategoryGroup?: boolean;
        ColumnClub?: boolean;
        ColumnRanking?: boolean;
        ColumnShowClockTime?: boolean;
        ColumnShowDistance?: boolean;
        ColumnShowLaps?: boolean;
        ColumnShowTrend?: boolean;
        ColumnTime?: boolean;
        ColumnTimePenalty?: boolean;
        DefaultToSplitlist?: boolean;
        EventID?: number;
        HideSplitList?: boolean;
        LiveEnabled?: boolean;
        LiveShooting?: boolean;
        LiveTracking?: boolean;
        ShowChipsOnLive?: boolean;
        ShowContestantList?: boolean;
        ShowDiploma?: boolean;
        ShowPanelResult?: boolean;
        ShowTotalLists?: boolean;
        EntityState?: 'Added' | 'Unchanged';
    }): ResultSetup {
        return new ResultSetup(values);
    }

    public static _FromServerJson(json: any): ResultSetup {
        return new ResultSetup({
            ID: json.ID,
            ColumnAfter: json.ColumnAfter,
            ColumnBib: json.ColumnBib,
            ColumnCategory: json.ColumnCategory,
            ColumnCategoryGroup: json.ColumnCategoryGroup,
            ColumnClub: json.ColumnClub,
            ColumnRanking: json.ColumnRanking,
            ColumnShowClockTime: json.ColumnShowClockTime,
            ColumnShowDistance: json.ColumnShowDistance,
            ColumnShowLaps: json.ColumnShowLaps,
            ColumnShowTrend: json.ColumnShowTrend,
            ColumnTime: json.ColumnTime,
            ColumnTimePenalty: json.ColumnTimePenalty,
            DefaultToSplitlist: json.DefaultToSplitlist,
            EventID: json.EventID,
            HideSplitList: json.HideSplitList,
            LiveEnabled: json.LiveEnabled,
            LiveShooting: json.LiveShooting,
            LiveTracking: json.LiveTracking,
            ShowChipsOnLive: json.ShowChipsOnLive,
            ShowContestantList: json.ShowContestantList,
            ShowDiploma: json.ShowDiploma,
            ShowPanelResult: json.ShowPanelResult,
            ShowTotalLists: json.ShowTotalLists,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of ResultSetup from another instance of ResultSetup.
     * @param other The ResultSetup you want to clone
     * @returns A cloned ResultSetup
     */
    public static FromOther(other: ResultSetup): ResultSetup {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return ResultSetup._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return ResultSetup.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'ColumnAfter'
            | 'ColumnBib'
            | 'ColumnCategory'
            | 'ColumnCategoryGroup'
            | 'ColumnClub'
            | 'ColumnRanking'
            | 'ColumnShowClockTime'
            | 'ColumnShowDistance'
            | 'ColumnShowLaps'
            | 'ColumnShowTrend'
            | 'ColumnTime'
            | 'ColumnTimePenalty'
            | 'DefaultToSplitlist'
            | 'EventID'
            | 'HideSplitList'
            | 'LiveEnabled'
            | 'LiveShooting'
            | 'LiveTracking'
            | 'ShowChipsOnLive'
            | 'ShowContestantList'
            | 'ShowDiploma'
            | 'ShowPanelResult'
            | 'ShowTotalLists'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'ResultSetup';
}
